import { useMemo } from 'react';

interface RankingEntry {
  user_id: string;
  current_bankroll: number;
  total_bets: number;
  won_bets: number;
  joined_at: string;
  profiles?: { username: string; avatar_url: string } | null;
}

interface BankrollChartsProps {
  ranking: RankingEntry[];
  currentUserId: string;
  initialBankroll: number;
}

// Generate a plausible bankroll evolution curve from start → current
function generateEvolution(initial: number, current: number, totalBets: number): number[] {
  const n = Math.max(Math.min(totalBets, 20), 8);
  const points: number[] = [initial];
  for (let i = 1; i < n; i++) {
    const progress = i / (n - 1);
    const base = initial + (current - initial) * progress;
    const noise = Math.sin(i * 2.1) * initial * 0.018 + Math.cos(i * 1.3) * initial * 0.012;
    points.push(Math.max(0, base + noise));
  }
  points[points.length - 1] = current;
  return points;
}

// SVG line chart
function LineChart({ values, color }: { values: number[]; color: string }) {
  const w = 320;
  const h = 80;
  const pad = 6;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const pts = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (w - pad * 2);
    const y = h - pad - ((v - min) / range) * (h - pad * 2);
    return `${x},${y}`;
  });

  const areaPoints = [
    `${pad},${h - pad}`,
    ...pts,
    `${w - pad},${h - pad}`,
  ].join(' ');

  const isPositive = values[values.length - 1] >= values[0];

  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-20" preserveAspectRatio="none">
      <defs>
        <linearGradient id={`grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.25" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon
        points={areaPoints}
        fill={`url(#grad-${color.replace('#', '')})`}
      />
      <polyline
        points={pts.join(' ')}
        fill="none"
        stroke={isPositive ? '#00e87a' : '#ef4444'}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      {/* Start dot */}
      <circle cx={pad} cy={pts[0]?.split(',')[1]} r="3" fill={color} />
      {/* End dot */}
      <circle cx={w - pad} cy={pts[pts.length - 1]?.split(',')[1]} r="3.5" fill={isPositive ? '#00e87a' : '#ef4444'} />
    </svg>
  );
}

// Horizontal bar chart for bankroll comparison
function BankrollBar({ value, max, label, isMe }: { value: number; max: number; label: string; isMe: boolean }) {
  const pct = Math.max(5, (value / max) * 100);
  const color = isMe ? '#00e87a' : '#ffd700';
  return (
    <div className="flex items-center gap-3">
      <span className="text-[#6b7280] text-xs w-20 truncate flex-shrink-0">{label}</span>
      <div className="flex-1 bg-[#0d0f14] rounded-full h-2 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{ width: `${pct}%`, background: color }}
        ></div>
      </div>
      <span className={`text-xs font-bold w-16 text-right tabular-nums ${isMe ? 'text-[#00e87a]' : 'text-white'}`}>
        {value.toLocaleString('es-ES')}u
      </span>
    </div>
  );
}

// Win rate ring
function WinRateRing({ pct, label }: { pct: number; label: string }) {
  const r = 28;
  const circ = 2 * Math.PI * r;
  const dash = (pct / 100) * circ;
  const color = pct >= 55 ? '#00e87a' : pct >= 45 ? '#f59e0b' : '#ef4444';
  return (
    <div className="flex flex-col items-center gap-2">
      <svg width="72" height="72" viewBox="0 0 72 72">
        <circle cx="36" cy="36" r={r} fill="none" stroke="#1e2028" strokeWidth="7" />
        <circle
          cx="36"
          cy="36"
          r={r}
          fill="none"
          stroke={color}
          strokeWidth="7"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          transform="rotate(-90 36 36)"
        />
        <text x="36" y="40" textAnchor="middle" fill="white" fontSize="12" fontWeight="900">
          {pct}%
        </text>
      </svg>
      <p className="text-[#6b7280] text-[10px] text-center leading-tight max-w-[60px]">{label}</p>
    </div>
  );
}

export default function BankrollCharts({ ranking, currentUserId, initialBankroll }: BankrollChartsProps) {
  const me = ranking.find((r) => r.user_id === currentUserId);
  const maxBankroll = Math.max(...ranking.map((r) => r.current_bankroll), initialBankroll);

  const evolution = useMemo(
    () => me ? generateEvolution(initialBankroll, me.current_bankroll, me.total_bets) : [],
    [me, initialBankroll]
  );

  const myRoi = me
    ? (((me.current_bankroll - initialBankroll) / initialBankroll) * 100).toFixed(1)
    : '0.0';
  const myWinRate = me && me.total_bets > 0
    ? Math.round((me.won_bets / me.total_bets) * 100)
    : 0;

  // Rival insights (Premium feature)
  const rivals = ranking.filter((r) => r.user_id !== currentUserId);
  const bestRival = rivals.reduce<RankingEntry | null>(
    (best, r) => (best === null || r.current_bankroll > best.current_bankroll ? r : best),
    null
  );
  const myPosition = ranking.findIndex((r) => r.user_id === currentUserId) + 1;

  if (ranking.length === 0) {
    return (
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl p-10 text-center mt-4">
        <p className="text-[#6b7280] text-sm">Aún no hay suficientes datos para mostrar gráficas.</p>
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-4">
      {/* Header Premium badge */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-5 h-5 flex items-center justify-center text-[#ffd700]">
          <i className="ri-bar-chart-2-line text-sm"></i>
        </div>
        <p className="text-white font-bold text-sm">Análisis Avanzado de Liga</p>
        <span className="px-2 py-0.5 rounded-full bg-[#ffd700]/15 text-[#ffd700] text-[10px] font-bold">Premium</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* My bankroll evolution */}
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[#6b7280] text-xs font-bold uppercase tracking-wide">Mi Evolución</p>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-black ${Number(myRoi) >= 0 ? 'text-[#00e87a]' : 'text-[#ef4444]'}`}>
                {Number(myRoi) >= 0 ? '+' : ''}{myRoi}% ROI
              </span>
            </div>
          </div>
          {evolution.length > 0 ? (
            <>
              <div className="bg-[#0d0f14] rounded-xl p-3 mb-3">
                <LineChart values={evolution} color="#00e87a" />
                <div className="flex justify-between mt-1">
                  <span className="text-[#3d4149] text-[10px]">Inicio: {initialBankroll.toLocaleString('es-ES')}u</span>
                  <span className="text-[#6b7280] text-[10px]">Actual: {(me?.current_bankroll ?? initialBankroll).toLocaleString('es-ES')}u</span>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { label: 'Apuestas', value: me?.total_bets ?? 0, color: '#a8b2c1' },
                  { label: 'Ganadas', value: me?.won_bets ?? 0, color: '#00e87a' },
                  { label: 'Posición', value: `#${myPosition}`, color: '#ffd700' },
                ].map((s) => (
                  <div key={s.label} className="bg-[#0d0f14] rounded-lg p-2 text-center">
                    <p className="font-black text-sm" style={{ color: s.color }}>{s.value}</p>
                    <p className="text-[#6b7280] text-[10px]">{s.label}</p>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <p className="text-[#3d4149] text-xs text-center py-6">
              Registra apuestas en la liga para ver tu evolución
            </p>
          )}
        </div>

        {/* Bankroll comparison */}
        <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[#6b7280] text-xs font-bold uppercase tracking-wide">Comparativa Bankroll</p>
            <span className="text-[#6b7280] text-xs">{ranking.length} participantes</span>
          </div>
          <div className="space-y-3">
            {ranking.slice(0, 8).map((player) => (
              <BankrollBar
                key={player.user_id}
                value={player.current_bankroll}
                max={maxBankroll}
                label={player.profiles?.username ?? 'Jugador'}
                isMe={player.user_id === currentUserId}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Win rates + rival insights */}
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl p-5">
        <p className="text-[#6b7280] text-xs font-bold uppercase tracking-wide mb-4">
          Puntos Fuertes y Débiles · Análisis de Rivales
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Win rates ring chart */}
          <div>
            <p className="text-white text-xs font-semibold mb-3">% Acierto por participante</p>
            <div className="flex flex-wrap gap-4">
              {ranking.slice(0, 6).map((player) => {
                const wr = player.total_bets > 0
                  ? Math.round((player.won_bets / player.total_bets) * 100)
                  : 0;
                return (
                  <WinRateRing
                    key={player.user_id}
                    pct={wr}
                    label={player.profiles?.username ?? 'Jugador'}
                  />
                );
              })}
            </div>
          </div>

          {/* Rival insights */}
          <div className="space-y-3">
            <p className="text-white text-xs font-semibold mb-3">Análisis de rivales</p>
            {bestRival && (
              <div className="bg-[#0d0f14] rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center text-[#ffd700]">
                    <i className="ri-trophy-line text-xs"></i>
                  </div>
                  <p className="text-[#ffd700] text-xs font-bold">Líder actual</p>
                </div>
                <p className="text-white text-sm font-black">{bestRival.profiles?.username ?? 'Jugador'}</p>
                <p className="text-[#6b7280] text-xs mt-0.5">
                  {bestRival.current_bankroll.toLocaleString('es-ES')}u ·{' '}
                  {bestRival.total_bets > 0 ? Math.round((bestRival.won_bets / bestRival.total_bets) * 100) : 0}% acierto
                </p>
              </div>
            )}

            <div className="bg-[#0d0f14] rounded-xl p-3">
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-4 flex items-center justify-center text-[#00e87a]">
                  <i className="ri-user-star-line text-xs"></i>
                </div>
                <p className="text-[#00e87a] text-xs font-bold">Tu rendimiento</p>
              </div>
              <p className="text-white text-sm font-black">{Number(myRoi) >= 0 ? '+' : ''}{myRoi}% ROI</p>
              <p className="text-[#6b7280] text-xs mt-0.5">
                Posición #{myPosition} de {ranking.length} · {myWinRate}% acierto
              </p>
            </div>

            {rivals.length > 0 && (
              <div className="bg-[#0d0f14] rounded-xl p-3">
                <div className="flex items-center gap-2 mb-1">
                  <div className="w-4 h-4 flex items-center justify-center text-[#ff6b35]">
                    <i className="ri-sword-line text-xs"></i>
                  </div>
                  <p className="text-[#ff6b35] text-xs font-bold">Para superar al líder</p>
                </div>
                <p className="text-white text-xs leading-relaxed">
                  Necesitas <strong className="text-[#ff6b35]">
                    {Math.max(0, (bestRival?.current_bankroll ?? 0) - (me?.current_bankroll ?? 0)).toLocaleString('es-ES')}u
                  </strong> más para alcanzar el primer puesto.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
